import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Button, Spinner } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getStateListById, saveStateData, updateStateData } from "../../redux/actions/StateActions";
import FormInput from "../../Components/FormInput";
import { useNavigate, useParams } from "react-router-dom";
import { logout } from "../../redux/actions/AuthActions";
import { toast } from "react-toastify";
import swal from 'sweetalert2';
import Preloader from "../../Components/Preloader";
import { Status } from "../../enums/Status";
import { Form } from "react-bootstrap";

const Dashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth, loading, errors },
    stateReducer: {
      stateLists,
      loadingState,
      errorsState,
      changedStateStatus,
    },
  } = useSelector((state) => state);

  useEffect(() => {
    if (id) {
      dispatch(getStateListById(auth?.accessToken, id));
    }
  }, [auth, id]);

  const [validated, setValidated] = useState(false);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    if (id) {
      dispatch(updateStateData(auth?.accessToken, formData, id));
    }
    else {
      dispatch(saveStateData(auth?.accessToken, formData));
    }
  };

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      if (errors?.status === 401) {
        dispatch(logout());
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors, dispatch, navigate]);

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (errorsState && Object.keys(errorsState).length > 0) {
      if (errorsState?.status === 409) {
        swal.fire({
          show: true,
          title: "Oops!",
          text: errorsState?.data?.message,
          icon: "error",
          showConfirmButton: false,
          timer: 1500
        });
        dispatch({ type: 'RESET_DELETE_STATE_STATUS' });
      } else if (errorsState?.status === 400) {
        // Transform errors array to field-based object
        if (errorsState?.data?.errors && Array.isArray(errorsState.data.errors)) {
          const transformedErrors = {};
          errorsState.data.errors.forEach(error => {
            if (error.field && error.message) {
              transformedErrors[error.field] = error.message;
            }
          });
          setFieldErrors(transformedErrors);
        }
      } else {
        setFieldErrors({});
        dispatch({ type: 'RESET_DELETE_STATE_STATUS' });
      }
    } else {
      setFieldErrors({});
    }
  }, [errorsState, dispatch]);

  useEffect(() => {
    if (changedStateStatus?.message) {
      swal.fire({
        show: true,
        title: "Well done!",
        text: changedStateStatus?.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      dispatch({ type: 'RESET_DELETE_STATE_STATUS' });
      navigate(-1);
    }
  }, [changedStateStatus, dispatch, navigate]);

  useEffect(() => {
    if (id) {
      if (stateLists && Object.keys(stateLists).length > 0) {
        setFormData({
          "nicStateCode": stateLists?.nicStateCode,
          "stateName": stateLists?.stateName,
          "status": stateLists?.status
        });
      }
    }

  }, [stateLists]);

  useEffect(() => {
    setFormData({
      status: Status.ACTIVE // Default status
    });
    dispatch({ type: 'RESET_DELETE_STATE_STATUS' });
  }, [dispatch]);

  const inputHandler = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field when user starts typing
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const inputHandlerCaps = (e) => {
    const { value, name } = e.target;
    const processedValue = value.replace(/\s+/g, '').toUpperCase(); // Remove spaces and convert to uppercase
    setFormData({ ...formData, [name]: processedValue });
    // Clear error for this field when user starts typing
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.nicStateCode ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.nicStateCode ? 'red' : base.borderColor,
      }
    }),
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("State"), path: "State" },
          { label: id ? t("Update State") :   t("Add State"), path: "/Add-State", active: true },
        ]}
        title={id ? t("Update State") : t("Add State")}
      />
      <Row>
        <Col lg={4}></Col>
        <Col lg={4}>
          <Card>
            <Card.Body>
              {errorsState?.data?.message && (
                <div className="alert alert-danger">
                  {errorsState.data.message}
                </div>
              )}
              <FormInput
                label={t("State Code")}
                type="text"
                errors={fieldErrors}
                value={formData.nicStateCode}
                name="nicStateCode"
                onChange={inputHandlerCaps}
                containerClass={"mb-3"}
              />

              <FormInput
                label={t("State Name")}
                type="text"
                errors={fieldErrors}
                value={formData.stateName}
                name="stateName"
                onChange={inputHandler}
                containerClass={"mb-3"}
              />

              <Form.Group className="mb-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Form.Label className="mb-0">{t("Status")}</Form.Label>
                  <Form.Check
                    type="switch"
                    id="status-switch"
                    label={formData.status === Status.ACTIVE ? t("Active") : t("Inactive")}
                    checked={formData.status === Status.ACTIVE}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        status: e.target.checked ? Status.ACTIVE : Status.INACTIVE
                      });
                    }}
                  />
                </div>
              </Form.Group>

              {loading ?
                <Button color="primary" disabled>
                  <Spinner
                    className="spinner-border-sm me-1"
                    tag="span"
                    color="white"
                  />
                  Please wait ...
                </Button>
                :
                <Button type="button" onClick={handleSubmit} disabled={loading}>
                  Submit
                </Button>
              }
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingState && (<Preloader />)}
    </>
  );
};

export default Dashboard;
